import type { ResellerPermission } from "@xop-any-time/shared";
import { AppBar, Box, Chip, IconButton, Link, Typography } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import {
  BookOpen,
  Briefcase,
  ClipboardList,
  Contact,
  FileText,
  GraduationCap,
  HardDriveDownload,
  Headset,
  KeyRound,
  LayoutDashboard,
  Menu,
  Target,
  Users,
  X, FileSignature } from "lucide-react";
import { useState, type JSX, type ReactNode } from "react";
import { NavLink } from "react-router-dom";

import { useAuth } from "../../auth/AuthProvider.js";
import { XOP_CONTACT } from "../../lib/company.js";
import { PUBLIC_ROUTES } from "../public/content.js";
import { NotificationBell } from "./notifications/NotificationBell.js";
import { ProfileMenu } from "./ProfileMenu.js";

/**
 * The signed-in chrome — the XOP command center's CRM, rebuilt here.
 *
 * This is a deliberate, close port of two files in `xop-edge-staff`:
 * `components/AppShell.tsx` (the sticky translucent app bar with the
 * stacked brand mark on the left and the profile block on the right) and
 * `crm/components/crm-layout.tsx` (the fixed 288px sidebar with
 * icon + label + count rows, and the `pl: { lg: "288px" }` content
 * offset). The measurements are copied, not approximated: 56px bar,
 * `alpha(background.paper, 0.85)` + `blur(12px)`, 288px rail, 16px lucide
 * icons at `mr: 1.5`, an active row at `alpha(primary.main, 0.1)` with
 * `primary.main` text at weight 600, and an outlined 18px-tall count chip
 * in tabular figures.
 *
 * It uses `primary` (XOP navy), NOT this app's teal `accent`. The teal is
 * the sign-in screen's job — it distinguishes the reseller portal from the
 * customer support portal at the front door. Once you are inside, this is
 * the CRM, and it should look like the CRM.
 *
 * WHAT IS DELIBERATELY ABSENT, so nobody "restores" it: the command
 * center's bar also carries a section-tab tray, global search, call
 * launcher, notification bell and an Administration group in the sidebar.
 * None of those have a counterpart here — a reseller has one section, no
 * staff notifications, and no admin surface — and rendering dead chrome
 * would be worse than not rendering it.
 *
 * THERE IS NO FOOTER, deliberately. The command center has none — its
 * chrome is app bar + rail + content, and the page simply ends. An earlier
 * pass added one here and it read as a different product immediately.
 *
 * THIS SUBTREE NO LONGER PINS THE LIGHT THEME (spec D §7.1). It used to:
 * `const CRM_THEME = getTheme("light")` wrapped everything below in its own
 * `ThemeProvider`, on the reasoning that the command center has no dark mode
 * and this is the command center. The product asked for a dark portal, and
 * the answer to that reasoning is that this is NOT the command center — it
 * is a partner-facing portal that borrows the command center's layout.
 *
 * So the shell now simply inherits the app-wide `ThemeModeProvider`, which
 * means ONE preference governs the whole app: the toggle a partner flips on
 * the sign-in screen is still set when they land here. Dark remains the
 * default, as it is on the front door.
 *
 * (Until 2026-09-14 one surface deliberately did NOT follow the mode: the
 * Settings page's printed-document preview. That page is gone — a reseller
 * brands nothing now — so every surface follows the mode.)
 */

/** Matches the command center's `--app-header-height`. The sidebar is
 * positioned from it, so the two must agree or the rail overlaps the bar. */
const HEADER_HEIGHT = 56;
const SIDEBAR_WIDTH = 288;

const footLink = {
  fontSize: 11.5,
  lineHeight: 1.6,
  color: "text.secondary",
  textDecoration: "none",
  "&:hover": { color: "text.primary", textDecoration: "underline" },
} as const;

interface NavItem {
  to: string;
  label: string;
  icon: typeof Users;
  end: boolean;
  /** The `view` key that gates this section (spec F §6.3). Undefined means
   * everyone with a session sees it — the Overview has no key of its own. */
  permission?: ResellerPermission;
}

/**
 * The surfaces a reseller gets, in the command center's own order: the
 * overview first, then Customers, then Invoices and Quotes as SEPARATE
 * entries — the CRM lists them separately because they are different
 * documents, and merging them into one tab here would be a gratuitous
 * divergence.
 *
 * Contacts follows Customers and precedes the documents: it is the people
 * behind the companies, curated here and mirrored in from each customer's
 * own contact people, so it belongs right after the companies themselves and
 * before Invoices and Quotes.
 *
 * After the CRM entries come what XOP PUBLISHES and what the company SIGNED:
 * Releases (the installer media and the guides that ship with a version),
 * Trainings (the training PDFs and videos for each release, 2026-09-17),
 * Legal (the company's own paperwork — "Documents" until 2026-09-17), and —
 * LAST — Documentation. Last on purpose (2026-09-14): it is the reference
 * shelf, read when a question comes up rather than as part of the day's work.
 * (It was called "Knowledge Base" from 2026-09-14 to 2026-09-17; renaming the
 * company-paperwork entry to "Legal" is what let it be "Documentation"
 * again without two look-alike entries side by side. Settings was last until
 * 2026-09-14; a reseller brands nothing now, so the page is gone.)
 */
const NAV: readonly NavItem[] = [
  { to: "/", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/customers", label: "Customers", icon: Users, end: false, permission: "customers:view" },
  // Prospects follows Customers: it is the leads XOP attached to this reseller
  // — potential customers — and sits with the companies (spec E §10.3). Read
  // only; the port strips the command center's write controls.
  { to: "/prospects", label: "Prospects", icon: Target, end: false, permission: "prospects:view" },
  { to: "/contacts", label: "Contacts", icon: Contact, end: false, permission: "contacts:view" },
  { to: "/invoices", label: "Invoices", icon: FileText, end: false, permission: "invoices:view" },
  { to: "/quotes", label: "Quotes", icon: Briefcase, end: false, permission: "invoices:view" },
  // Purchase orders follow Quotes (2026-09-17): an accepted quote becomes a
  // purchase order — the reseller's order TO XOP Networks, the one document in
  // this group that points the other way. Same `invoices:*` keys as the quotes
  // beside it; there is no permission section of its own.
  { to: "/purchase-orders", label: "Purchase orders", icon: ClipboardList, end: false, permission: "invoices:view" },
  // Licences sits AFTER the money documents: a reseller quotes, invoices and
  // then licenses, and grouping it with the customer-facing surfaces keeps
  // that order visible. It is this portal's own screen — staff set the same
  // fields through the CRM's Agreement modal, which is not a licence and
  // issues no file.
  { to: "/licences", label: "Licences", icon: KeyRound, end: false, permission: "licences:view" },
  // The rail says "Software Releases" and "Online Training" (CEO, 2026-11-02) —
  // the same words the support portal and the command center use. The URLs
  // (/releases, /trainings) did not change.
  // Releases follows the licence a technician has just issued: the installer
  // media for the box, the change log — which IS the release notes, and the
  // only copy of them — and the guides that ship with the version. It is the one place in the fleet outside the command center
  // where the Disk 1 / Disk 2 images are reachable at all. Gated on
  // `documentation:view` — "may read what XOP publishes". (It was
  // "Downloads" until 2026-09-14; now it is the command center's Releases
  // pages, read-only.)
  { to: "/releases", label: "Software Releases", icon: HardDriveDownload, end: false, permission: "documentation:view" },
  // Trainings follows Releases (2026-09-17): the training PDFs and videos XOP
  // publishes for each release, on their own page rather than on the release
  // (CEO). Same "may read what XOP publishes" gate.
  { to: "/trainings", label: "Online Training", icon: GraduationCap, end: false, permission: "documentation:view" },
  // Legal is the company's OWN paperwork — the agreements it signed with XOP
  // and its file cabinet. NO permission (CEO, 2026-09-17): every member of
  // the company sees the entry, even before anything is filed, and the page
  // says so. Filing into the cabinet still needs `documents:edit`.
  { to: "/legal", label: "Legal", icon: FileSignature, end: false },
  // Documentation closes the rail — see the note above the array. Pinned last
  // by `AppShell.navOrder.test.tsx`.
  { to: "/documentation", label: "Documentation", icon: BookOpen, end: false, permission: "documentation:view" },
];

/** `styled(NavLink)` so react-router's automatic `.active` class drives the
 * highlight — the same mechanism the command center's nav uses, rather
 * than threading `useLocation` through every row. */
const NavRow = styled(NavLink)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1, 1.5),
  fontSize: 14,
  fontWeight: 500,
  borderRadius: theme.shape.borderRadius,
  textDecoration: "none",
  color: theme.palette.text.secondary,
  transition: theme.transitions.create(["background-color", "color"], {
    duration: 150,
  }),
  "& .nav-icon": { color: theme.palette.text.disabled, display: "flex", marginRight: 12 },
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
    "& .nav-icon": { color: theme.palette.text.secondary },
  },
  "&.active": {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
    fontWeight: 600,
    "& .nav-icon": { color: theme.palette.primary.main },
  },
}));

export interface NavCounts {
  customers?: number | null;
  prospects?: number | null;
  invoices?: number | null;
  quotes?: number | null;
}

export function AppShell({
  children,
  counts,
}: {
  children: ReactNode;
  /** Sidebar count badges. Undefined renders no chip at all rather than a
   * "0" — the command center's rows do the same, and a confident zero on a
   * figure that simply has not loaded is worse than no figure. */
  counts?: NavCounts;
}): JSX.Element {
  const { user, can } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const badgeFor = (item: NavItem): string | null => {
    const value =
      item.to === "/customers"
        ? counts?.customers
        : item.to === "/prospects"
          ? counts?.prospects
          : item.to === "/invoices"
            ? counts?.invoices
            : item.to === "/quotes"
              ? counts?.quotes
              : null;
    return typeof value === "number" ? value.toLocaleString() : null;
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", color: "text.primary" }}>
      <AppBar
        position="sticky"
        sx={{
          bgcolor: (t) => alpha(t.palette.background.paper, 0.85),
          backdropFilter: "blur(12px)",
          borderBottom: 1,
          borderColor: "divider",
          zIndex: (t) => t.zIndex.appBar,
        }}
      >
        <Box
          sx={{
            display: "flex",
            width: "100%",
            minHeight: HEADER_HEIGHT,
            alignItems: "center",
            gap: { xs: 0.5, md: 2 },
            px: { xs: 1, sm: 2, md: 3 },
          }}
        >
          <IconButton
            aria-label="Open navigation menu"
            onClick={() => setSidebarOpen(true)}
            edge="start"
            sx={{ display: { lg: "none" }, color: "text.secondary", flexShrink: 0 }}
          >
            <Menu size={20} />
          </IconButton>

          <BrandMark />

          <Box sx={{ flex: 1 }} />

          {/* What changed since this person last looked — overdue invoices,
              quotes waiting, licences lapsing, new versions (2026-09-14). */}
          {user && <NotificationBell />}
          {user && <ProfileMenu user={user} />}
        </Box>
      </AppBar>

      {/* Mobile scrim, below the bar (z-index 10 < appBar) — the command
          center's sidebar uses exactly this arrangement. */}
      {sidebarOpen && (
        <Box
          onClick={() => setSidebarOpen(false)}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 10,
            bgcolor: "rgba(0,0,0,0.5)",
            display: { lg: "none" },
          }}
        />
      )}

      <Box
        component="aside"
        sx={{
          position: "fixed",
          left: 0,
          bottom: 0,
          top: `${HEADER_HEIGHT}px`,
          zIndex: 10,
          width: SIDEBAR_WIDTH,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.paper",
          borderRight: 1,
          borderColor: "divider",
          transition: "transform 300ms ease-in-out",
          transform: sidebarOpen ? "translateX(0)" : "translateX(-100%)",
          "@media (min-width:1200px)": { transform: "translateX(0)" },
        }}
      >
        <Box sx={{ display: { xs: "flex", lg: "none" }, justifyContent: "flex-end", p: 1 }}>
          <IconButton size="small" onClick={() => setSidebarOpen(false)} aria-label="Close menu">
            <X size={20} />
          </IconButton>
        </Box>

        <Box
          component="nav"
          aria-label="Primary"
          sx={{ pt: { xs: 2, lg: 2.5 }, px: 1.5, pb: 3, "& > * + *": { mt: 0.25 } }}
        >
          {NAV.filter((item) => item.permission === undefined || can(item.permission)).map((item) => {
            const Icon = item.icon;
            const badge = badgeFor(item);
            return (
              <NavRow
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setSidebarOpen(false)}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box className="nav-icon">
                    <Icon size={16} />
                  </Box>
                  {item.label}
                </Box>
                {badge !== null && (
                  <Chip
                    variant="outlined"
                    size="small"
                    label={badge}
                    {...(item.to === "/invoices" ? { title: `${badge} unpaid`, "aria-label": `${badge} unpaid` } : {})}
                    sx={{
                      height: 18,
                      fontSize: 10,
                      fontWeight: 500,
                      fontVariantNumeric: "tabular-nums",
                      "& .MuiChip-label": { px: 0.75 },
                    }}
                  />
                )}
              </NavRow>
            );
          })}
        </Box>

        {/* The rail's foot. Since 2026-09-15 it is a LINK to the Contact
            page, the legal links and the copyright — no longer the whole
            contact block (address, phone, both mailboxes, website), which
            crowded the menu and was cut off on a short screen. Those details
            live on `/contact`, from the same `lib/company.ts`. */}
        <Box
          component="footer"
          aria-label="XOP Networks"
          data-testid="rail-footer"
          sx={{ mt: "auto", px: 1.5, pt: 1.5, pb: 2.5, borderTop: 1, borderColor: "divider" }}
        >
          <NavRow to="/contact" onClick={() => setSidebarOpen(false)}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box className="nav-icon">
                <Headset size={16} />
              </Box>
              Contact XOP Networks
            </Box>
          </NavRow>
          <Box sx={{ px: 1.5 }}>
            <Box sx={{ display: "flex", gap: 1.5, mt: 1.25 }}>
              <Link component={NavLink} to={PUBLIC_ROUTES.terms} sx={footLink}>Terms of Use</Link>
              <Link component={NavLink} to={PUBLIC_ROUTES.privacy} sx={footLink}>Privacy Policy</Link>
            </Box>
            <Typography sx={{ fontSize: 11, color: "text.disabled", mt: 1 }}>
              © {new Date().getFullYear()} {XOP_CONTACT.company} All rights reserved.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ pl: { lg: `${SIDEBAR_WIDTH}px` } }}>
        <Box component="main" sx={{ pt: 2, pb: 4 }}>
          <Box sx={{ px: { xs: 2, sm: 2.5, lg: 3 } }}>{children}</Box>
        </Box>
      </Box>
    </Box>
  );
}

/** The command center's stacked brand mark: the COMPANY as the wordmark
 * ("XOP Networks" — product owner, 2026-09-09: the product name belongs on
 * the sign-in nameplate, not here) with "Reseller portal" as the small
 * uppercase tagline underneath. Same 24px swirl, same 14px/700 wordmark. */
function BrandMark(): JSX.Element {
  return (
    <Box
      component={NavLink}
      to="/"
      aria-label="XOP Networks — reseller portal"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        textDecoration: "none",
        flexShrink: 0,
        minWidth: 0,
      }}
    >
      <Box
        component="img"
        src="/xop-mark.svg"
        alt=""
        aria-hidden
        draggable={false}
        sx={{ height: 24, width: 24, flexShrink: 0 }}
      />
      <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", minWidth: 0 }}>
        <Typography
          component="span"
          sx={{
            whiteSpace: "nowrap",
            fontSize: 14,
            fontWeight: 700,
            lineHeight: 1.15,
            letterSpacing: "-0.01em",
            color: "text.primary",
          }}
        >
          XOP Networks
        </Typography>
        <Typography
          component="span"
          sx={{
            display: { xs: "none", sm: "block" },
            whiteSpace: "nowrap",
            fontSize: 10,
            fontWeight: 500,
            lineHeight: 1.2,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "text.disabled",
          }}
        >
          Reseller portal
        </Typography>
      </Box>
    </Box>
  );
}
